import { ChainId } from '@uniswap/sdk-core'
import useHttpLocations from 'hooks/useHttpLocations'
import { useMemo } from 'react'
import { isAddress } from 'utils'

import EthereumLogo from '../../assets/images/ethereum-logo.png'
import AvaxLogo from '../../assets/svg/avax_logo.svg'
import BnbLogo from '../../assets/svg/bnb-logo.svg'
import CeloLogo from '../../assets/svg/celo_logo.svg'
import MaticLogo from '../../assets/svg/matic-token-icon.svg'
import BeraLogo from '../../assets/svg/berachain_logo.svg'
import HypeLogo from '../../assets/images/hyperliquid-logo.png'
import SonicLogo from '../../assets/images/sonic-logo.png'
import MitosisLogo from '../../assets/images/mitosis-logo.png'
import SomniaLogo from '../../assets/images/somnia-logo.png'
import ZerogLogo from '../../assets/images/zerog-logo.png'
import PlasmaLogo from '../../assets/images/plasma-logo.png'
import MonadTestnetLogo from '../../assets/images/monad-testnet-logo.png'
import MonadLogo from '../../assets/images/monad-logo.png'
import StableLogo from '../../assets/images/stable-logo.png'
import { isCelo, isBerachain, isHyperliquid, isSonic, isMitosis, isSomnia, isZeroG, isPlasma, isMonadTestnet, isMonad, isStable, NATIVE_CHAIN_ID, nativeOnChain } from '../../constants/tokens'

type Network = 'ethereum' | 'arbitrum' | 'optimism' | 'polygon' | 'smartchain' | 'celo' | 'avalanchec' | 'berachain' | 'hyperliquid' | 'sonic' | 'katana' | 'mitosis' | 'somnia' | 'zerog' | 'plasma' | 'monad-testnet' | 'monad' | 'stable'

export function chainIdToNetworkName(networkId: ChainId): Network {
  switch (networkId) {
    case ChainId.MAINNET:
      return 'ethereum'
    case ChainId.ARBITRUM_ONE:
      return 'arbitrum'
    case ChainId.OPTIMISM:
      return 'optimism'
    case ChainId.POLYGON:
      return 'polygon'
    case ChainId.BNB:
      return 'smartchain'
    case ChainId.CELO:
      return 'celo'
    case ChainId.AVALANCHE:
      return 'avalanchec'
    case ChainId.BERACHAIN:
      return 'berachain'
    case ChainId.HYPERLIQUID:
      return 'hyperliquid'
    case ChainId.SONIC:
      return 'sonic'
    case ChainId.KATANA:
      return 'katana'
    case ChainId.MITOSIS:
      return 'mitosis'
    case ChainId.SOMNIA:
      return 'somnia'
    case ChainId.ZEROG:
      return 'zerog'
    case ChainId.PLASMA:
      return 'plasma'
    case ChainId.MONAD_TESTNET:
      return 'monad-testnet'
    case ChainId.MONAD:
      return 'monad'
    case ChainId.STABLE:
      return 'stable'
    default:
      return 'ethereum'
  }
}

export function getNativeLogoURI(chainId: ChainId = ChainId.MAINNET): string {
  switch (chainId) {
    case ChainId.POLYGON:
    case ChainId.POLYGON_MUMBAI:
      return MaticLogo
    case ChainId.BNB:
      return BnbLogo
    case ChainId.CELO:
    case ChainId.CELO_ALFAJORES:
      return CeloLogo
    case ChainId.AVALANCHE:
      return AvaxLogo
    case ChainId.BERACHAIN:
      return BeraLogo
    case ChainId.HYPERLIQUID:
      return HypeLogo
    case ChainId.SONIC:
      return SonicLogo
    case ChainId.MITOSIS:
      return MitosisLogo
    case ChainId.SOMNIA:
      return SomniaLogo
    case ChainId.ZEROG:
      return ZerogLogo
    case ChainId.PLASMA:
      return PlasmaLogo
    case ChainId.MONAD_TESTNET:
      return MonadTestnetLogo
    case ChainId.MONAD:
      return MonadLogo
    case ChainId.STABLE:
      return StableLogo
    default:
      return EthereumLogo
  }
}

function getTokenLogoURI(address: string, chainId: ChainId = ChainId.MAINNET): string | void {
  const networkName = chainIdToNetworkName(chainId)
  const networksWithUrls = [ChainId.ARBITRUM_ONE, ChainId.MAINNET, ChainId.OPTIMISM, ChainId.BNB, ChainId.AVALANCHE]

  if (isCelo(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return CeloLogo
  }

  if (isBerachain(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return BeraLogo
  }

  if (isHyperliquid(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return HypeLogo
  }

  if (isSonic(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return SonicLogo
  }

  if (isMitosis(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return MitosisLogo
  }

  if (isSomnia(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return SomniaLogo
  }

  if (isZeroG(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return ZerogLogo
  }

  if (isPlasma(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return PlasmaLogo
  }

  if (isMonadTestnet(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return MonadTestnetLogo
  }

  if (isStable(chainId) && address === nativeOnChain(chainId).wrapped.address) {
    return StableLogo
  }

  if (networksWithUrls.includes(chainId)) {
    return `https://raw.githubusercontent.com/Uniswap/assets/master/blockchains/${networkName}/assets/${address}/logo.png`
  }
}

export default function useCurrencyLogoURIs(
  currency:
    | {
        isNative?: boolean
        isToken?: boolean
        address?: string
        chainId: number
        logoURI?: string | null
      }
    | null
    | undefined
): string[] {
  const locations = useHttpLocations(currency?.logoURI)
  return useMemo(() => {
    const logoURIs = [...locations]
    if (currency) {
      if (currency.isNative || currency.address === NATIVE_CHAIN_ID) {
        logoURIs.push(getNativeLogoURI(currency.chainId))
      } else if (currency.isToken || currency.address) {
        const checksummedAddress = isAddress(currency.address)
        const logoURI = checksummedAddress && getTokenLogoURI(checksummedAddress, currency.chainId)
        if (logoURI) {
          logoURIs.push(logoURI)
        }
      }
    }
    return logoURIs
  }, [currency, locations])
}
